import { useEffect, useRef, useState } from 'react';
import type { DemoKey, DemoScript, FingerKey, Fx, Pt, RoundProps, ViewProps } from '../kit.js';
import { BOARD, BoardFrame, CLEAR_MS, clamp, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';
import { CRATE_LEVELS, cutCrate, extent, fits, key, normalise, turn, turned } from './logic.js';
import type { Cell } from './logic.js';
import { CRATE_PAL, GOODS, shade } from './shared.js';

/**
 * QUARTERMASTER · PACK THE CRATE — drag the blocks in until the crate is full.
 *
 * An open crate, seen from above, and a row of blocks of stock on the shelf below it. Drag a block
 * into the crate and it settles into the nearest place it fits; tap a block on the shelf to turn it
 * a quarter; tap a block in the crate to take it out again. The blocks were cut from a full crate,
 * so they always fill it exactly, and a block that doesn't fit simply comes back — nothing here is
 * a mistake. Rounds 1 and 2 are a 3 × 3 crate in three blocks of three (round 2's cells a little
 * smaller); round 3 a 4 × 3 crate in four blocks.
 *
 * A converted board (kit.tsx): the layout and the settling as plain functions, a small view state
 * (`PackView`), a pure `View`, the `Board` that plays it, and the `demo` the how-to card plays.
 */

const W = BOARD.w;
const H = BOARD.h;
/** A crate cell's size in units, by round (easier since 6 Oct 2026: the old 60 and 54, and halfway), and the crate's wooden frame. */
const CELL = [60, 57, 54] as const;
const FRAME = 12;
/** Between the crate and the shelf, and the shelf's height. */
const TRAY_GAP = 10;
const TRAY_H = 92;
/** How far above the finger a block rides while dragged, so the thumb doesn't hide it. */
const LIFT = 22;
/** How far a finger moves before a press becomes a drag, in units. */
const DRAG = 8;
/** A block on the shelf turns over this long. */
const TURN_MS = 200;
/** "Doesn't fit" shows this long. */
const NOPE_MS = 1_600;

export interface PieceState {
  /** The block as it lies now (turned). */
  cells: readonly Cell[];
  /** Where it sits in the crate (its top left cell), or null on the shelf. */
  at: Cell | null;
}

/**
 * Where everything is for a crate of `cols` × `rows` cells of `c` units, crate and shelf together
 * in the middle of the board. `dims` is each block's longest side in cells (the same however it is
 * turned), so each slot on the shelf is as wide as its block needs.
 */
export function packLayout(cols: number, rows: number, c: number, dims: readonly number[]) {
  const outerW = cols * c + FRAME * 2;
  const outerH = rows * c + FRAME * 2;
  const crateX = Math.round((W - outerW) / 2);
  const crateY = Math.round((H - outerH - TRAY_GAP - TRAY_H) / 2);
  const trayTop = crateY + outerH + TRAY_GAP;
  const gap = 4;
  const pad = 10;
  const n = dims.length;
  const room = W - 12 - gap * (n - 1) - pad * n;
  const tc = Math.max(12, Math.min(28, Math.floor(room / Math.max(1, dims.reduce((a, b) => a + b, 0))), Math.floor((TRAY_H - 12) / 3)));
  const widths = dims.map((d) => d * tc + pad);
  const used = widths.reduce((a, b) => a + b, 0) + gap * (n - 1);
  const lefts: number[] = [];
  let x = (W - used) / 2;
  for (const w of widths) {
    lefts.push(x);
    x += w + gap;
  }
  return {
    lv: { cols, rows },
    c,
    crateX,
    crateY,
    outerW,
    outerH,
    gx: crateX + FRAME,
    gy: crateY + FRAME,
    trayTop,
    /** Shelf slot `i`: its centre, and its left and right edges. */
    slot: (i: number) => ({ x: lefts[i]! + widths[i]! / 2, y: trayTop + TRAY_H / 2, left: lefts[i]!, right: lefts[i]! + widths[i]! }),
    gap,
    /** A shelf block's cell size. */
    tc,
  };
}
type Layout = ReturnType<typeof packLayout>;

/** Each block's longest side in cells. */
const dimsOf = (pieces: readonly { cells: readonly Cell[] }[]) => pieces.map((p) => Math.max(...extent(p.cells)));

/** The cells taken by every block in the crate but `skip`. */
function filledBy(pieces: readonly PieceState[], skip: number): Set<string> {
  const set = new Set<string>();
  pieces.forEach((p, i) => {
    if (i === skip || !p.at) return;
    for (const [c, r] of p.cells) set.add(key(p.at[0] + c, p.at[1] + r));
  });
  return set;
}

/** Where a block with its top left at `x`,`y` (units) would settle: the nearest place it fits, within a cell. */
export function settleAt(
  cells: readonly Cell[],
  x: number,
  y: number,
  L: { gx: number; gy: number; c: number; lv: { cols: number; rows: number } },
  filled: ReadonlySet<string>,
): Cell | null {
  const fc = (x - L.gx) / L.c;
  const fr = (y - L.gy) / L.c;
  let best: { at: Cell; d: number } | null = null;
  for (let dc = -1; dc <= 1; dc++) {
    for (let dr = -1; dr <= 1; dr++) {
      const at: Cell = [Math.round(fc) + dc, Math.round(fr) + dr];
      const d = Math.hypot(at[0] - fc, at[1] - fr);
      if (d > 0.95 || !fits(cells, at, filled, L.lv.cols, L.lv.rows)) continue;
      if (!best || d < best.d) best = { at, d };
    }
  }
  return best?.at ?? null;
}

/** A block for a view state: its cells as digit pairs (column, row), and `@` and its top left once in the crate: "001020@01". */
export function pieceCode(p: PieceState): string {
  return p.cells.map(([c, r]) => `${c}${r}`).join('') + (p.at ? `@${p.at[0]}${p.at[1]}` : '');
}

/** A block back from its code. */
export function pieceOf(code: string): PieceState {
  const [cells = '', at] = code.split('@');
  const out: Cell[] = [];
  for (let k = 0; k + 1 < cells.length; k += 2) out.push([Number(cells[k]), Number(cells[k + 1])]);
  return { cells: out, at: at && at.length >= 2 ? [Number(at[0]), Number(at[1])] : null };
}

/** Everything the crate and the shelf show. */
export interface PackView {
  /** The game clock, ms. */
  t: number;
  /** The crate: columns, rows, and a cell's size in units. */
  g: readonly [cols: number, rows: number, cell: number];
  /** The blocks, in their shelf order (`pieceCode`). */
  b: readonly string[];
  /** A block in hand: which, and its top left (units). */
  d: readonly [i: number, x: number, y: number] | null;
  /** A block just turned on the shelf: which, and how far through its quarter turn (0–1). */
  tn: readonly [i: number, f: number] | null;
  /** A block that didn't fit was just let go over the crate. */
  no: boolean;
  /** Full: the lid coming down, 0–1 (null until then). */
  lid: number | null;
  fx?: Fx | undefined;
}

/** One block drawn at cell size `s`, its top left at 0,0. */
function Block({ cells, s, index, id }: { cells: readonly Cell[]; s: number; index: number; id: string }) {
  const fill = GOODS[index % GOODS.length]!;
  const inset = Math.max(1.2, s * 0.04);
  const set = new Set(cells.map(([c, r]) => key(c, r)));
  return (
    <g>
      {/* bridges between neighbouring cells, so a block reads as one lot */}
      {cells.map(([c, r]) => (
        <g key={`b${c},${r}`}>
          {set.has(key(c + 1, r)) && <rect x={c * s + s - inset * 2} y={r * s + s * 0.3} width={inset * 4} height={s * 0.4} fill={shade(fill, -0.25)} />}
          {set.has(key(c, r + 1)) && <rect x={c * s + s * 0.3} y={r * s + s - inset * 2} width={s * 0.4} height={inset * 4} fill={shade(fill, -0.25)} />}
        </g>
      ))}
      {cells.map(([c, r]) => (
        <g key={`${c},${r}`} transform={`translate(${c * s} ${r * s})`}>
          <rect x={inset} y={inset} width={s - inset * 2} height={s - inset * 2} rx={s * 0.14} fill={`url(#${id}g${index % GOODS.length})`} stroke={shade(fill, -0.5)} strokeWidth="1" />
          <rect x={inset + s * 0.08} y={inset + s * 0.06} width={s * 0.6} height={Math.max(1.5, s * 0.07)} rx={s * 0.03} fill="#ffffff" opacity=".35" />
          <rect x={s * 0.44} y={inset} width={s * 0.12} height={s - inset * 2} fill={shade(fill, 0.3)} opacity=".45" />
        </g>
      ))}
    </g>
  );
}

/** The crate and the shelf, from their view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<PackView>) {
  const id = useSvgId();
  const skin = ERA_SKIN[era];
  const pal = CRATE_PAL[era];
  const [cols, rows, c] = view.g;
  const pieces = view.b.map(pieceOf);
  const L: Layout = packLayout(cols, rows, c, dimsOf(pieces));
  const filled = pieces.reduce((s, p) => s + (p.at ? p.cells.length : 0), 0);
  const total = cols * rows;
  // A demo glides every number between its keys: which block is read rounded.
  const drag = view.d;
  const held = drag ? Math.round(drag[0]) : -1;
  const inHand = pieces[held];
  const preview = drag && inHand ? settleAt(inHand.cells, drag[1], drag[2], L, filledBy(pieces, held)) : null;
  const turning = view.tn ? Math.round(view.tn[0]) : -1;
  const done = view.lid !== null;
  const status = done
    ? { text: say(lang, '✓ PACKED', '✓ PAKKET'), tone: 'ok' as const }
    : view.no
      ? { text: say(lang, "DOESN'T FIT — TURN IT", 'PASSER IKKE – SNU DEN'), tone: 'warn' as const }
      : { text: say(lang, 'FILLED', 'FYLT'), count: [filled, total] as const, hint: say(lang, 'TAP A BLOCK TO TURN', 'TRYKK FOR Å SNU') };
  // The lid comes down once the crate is full, quickly at first.
  const lid = done ? 1 - (1 - clamp(view.lid! * 1.6, 0, 1)) ** 2 : 0;

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        {GOODS.map((g, i) => (
          <linearGradient key={i} id={`${id}g${i}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={shade(g, 0.22)} />
            <stop offset="1" stopColor={shade(g, -0.22)} />
          </linearGradient>
        ))}
        <linearGradient id={`${id}wood`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={shade(pal.top, 0.08)} />
          <stop offset="1" stopColor={shade(pal.body, -0.1)} />
        </linearGradient>
        <linearGradient id={`${id}floor`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={shade(pal.body, -0.72)} />
          <stop offset="1" stopColor={shade(pal.body, -0.5)} />
        </linearGradient>
        <linearGradient id={`${id}shade`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#000" stopOpacity=".55" />
          <stop offset="1" stopColor="#000" stopOpacity="0" />
        </linearGradient>
        <radialGradient id={`${id}lamp`} cx=".5" cy=".35" r=".7">
          <stop offset="0" stopColor={skin.haze} stopOpacity=".16" />
          <stop offset="1" stopColor={skin.haze} stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect x="0" y="0" width={W} height={L.trayTop} fill={`url(#${id}lamp)`} />

      {/* the crate: its frame, floor and the lines of its cells */}
      <rect x={L.crateX + 3} y={L.crateY + 5} width={L.outerW} height={L.outerH} rx="7" fill="#000" opacity=".45" />
      <rect x={L.crateX} y={L.crateY} width={L.outerW} height={L.outerH} rx="7" fill={`url(#${id}wood)`} stroke={pal.edge} strokeWidth="1.5" />
      <g stroke={pal.edge} strokeOpacity=".5" strokeWidth="1">
        <path d={`M${L.crateX + 4} ${L.crateY + FRAME / 2} H${L.crateX + L.outerW - 4} M${L.crateX + 4} ${L.crateY + L.outerH - FRAME / 2} H${L.crateX + L.outerW - 4}`} />
        <path d={`M${L.crateX + FRAME / 2} ${L.crateY + 4} V${L.crateY + L.outerH - 4} M${L.crateX + L.outerW - FRAME / 2} ${L.crateY + 4} V${L.crateY + L.outerH - 4}`} />
      </g>
      {[
        [L.crateX, L.crateY, 1, 1],
        [L.crateX + L.outerW, L.crateY, -1, 1],
        [L.crateX, L.crateY + L.outerH, 1, -1],
        [L.crateX + L.outerW, L.crateY + L.outerH, -1, -1],
      ].map(([x, y, sx, sy], i) => (
        <g key={i}>
          <path d={`M${x! + sx! * 2} ${y! + sy! * 16} V${y! + sy! * 2} H${x! + sx! * 16}`} stroke={pal.metal} strokeWidth="3" fill="none" strokeLinecap="round" />
          <circle cx={x! + sx! * 6} cy={y! + sy! * 6} r="1.4" fill={shade(pal.metal, -0.4)} />
        </g>
      ))}
      <rect x={L.gx} y={L.gy} width={cols * c} height={rows * c} fill={`url(#${id}floor)`} />
      <g stroke="#000" strokeOpacity=".35" strokeWidth="1">
        {Array.from({ length: cols - 1 }, (_, k) => (
          <line key={`v${k}`} x1={L.gx + (k + 1) * c} y1={L.gy} x2={L.gx + (k + 1) * c} y2={L.gy + rows * c} />
        ))}
        {Array.from({ length: rows - 1 }, (_, k) => (
          <line key={`h${k}`} x1={L.gx} y1={L.gy + (k + 1) * c} x2={L.gx + cols * c} y2={L.gy + (k + 1) * c} />
        ))}
      </g>

      {/* where the block in hand would settle */}
      {inHand && preview && (
        <g transform={`translate(${L.gx + preview[0] * c} ${L.gy + preview[1] * c})`}>
          {inHand.cells.map(([cc, rr]) => (
            <rect key={key(cc, rr)} x={cc * c + 2} y={rr * c + 2} width={c - 4} height={c - 4} rx="6" fill={accent} fillOpacity=".18" stroke={accent} strokeWidth="1.6" strokeDasharray="5 3" />
          ))}
        </g>
      )}

      {/* the blocks in the crate */}
      {pieces.map((p, i) =>
        p.at ? (
          <g key={`in${i}`} transform={`translate(${L.gx + p.at[0] * c} ${L.gy + p.at[1] * c})`}>
            <Block cells={p.cells} s={c} index={i} id={id} />
          </g>
        ) : null,
      )}
      {/* the crate's inner shadow, over its floor and the blocks in it */}
      <rect x={L.gx} y={L.gy} width={cols * c} height="12" fill={`url(#${id}shade)`} />
      <rect x={L.gx} y={L.gy} width="8" height={rows * c} fill="#000" opacity=".16" />

      {/* the shelf */}
      <rect x="6" y={L.trayTop} width={W - 12} height={TRAY_H} rx="10" fill="#000" opacity=".28" />
      <line x1="14" y1={L.trayTop + TRAY_H - 10} x2={W - 14} y2={L.trayTop + TRAY_H - 10} stroke={skin.ink} strokeOpacity=".25" />
      {pieces.map((p, i) => {
        const at = L.slot(i);
        if (p.at || held === i) return <circle key={`s${i}`} cx={at.x} cy={at.y} r="3" fill={skin.ink} opacity=".3" />;
        const [w, h] = extent(p.cells);
        // A block just turned comes round its last quarter.
        const spin = turning === i && view.tn ? -90 * (1 - clamp(view.tn[1], 0, 1)) : 0;
        return (
          <g key={`s${i}`} transform={`rotate(${spin.toFixed(1)} ${at.x} ${at.y}) translate(${(at.x - (w * L.tc) / 2).toFixed(1)} ${(at.y - (h * L.tc) / 2).toFixed(1)})`}>
            <Block cells={p.cells} s={L.tc} index={i} id={id} />
          </g>
        );
      })}

      {/* the block in hand, with its shadow on the board */}
      {drag && inHand && (
        <g transform={`translate(${drag[1].toFixed(1)} ${drag[2].toFixed(1)})`}>
          <g transform="translate(5 8)" opacity=".4">
            {inHand.cells.map(([cc, rr]) => (
              <rect key={key(cc, rr)} x={cc * c + 2} y={rr * c + 2} width={c - 4} height={c - 4} rx="7" fill="#000" />
            ))}
          </g>
          <Block cells={inHand.cells} s={c} index={held} id={id} />
        </g>
      )}

      {/* full: the lid comes down */}
      {done && (
        <g transform={`translate(0 ${(-(L.crateY + L.outerH + 20) * (1 - lid)).toFixed(1)})`}>
          <rect x={L.crateX} y={L.crateY} width={L.outerW} height={L.outerH} rx="7" fill={`url(#${id}wood)`} stroke={pal.edge} strokeWidth="1.5" />
          {Array.from({ length: 4 }, (_, k) => (
            <line
              key={k}
              x1={L.crateX + 4}
              y1={L.crateY + ((k + 1) * L.outerH) / 5}
              x2={L.crateX + L.outerW - 4}
              y2={L.crateY + ((k + 1) * L.outerH) / 5}
              stroke={pal.edge}
              strokeOpacity=".55"
            />
          ))}
          <text x={L.crateX + L.outerW / 2} y={L.crateY + L.outerH / 2 + 12} textAnchor="middle" fontSize="34" fontWeight="700" fontFamily={mono} fill={pal.mark} opacity=".85">
            ✓
          </text>
        </g>
      )}
    </BoardFrame>
  );
}

function Board(props: RoundProps) {
  const { level, lang } = props;
  const lv = CRATE_LEVELS[level]!;
  const c = CELL[level];
  const [first] = useState<PieceState[]>(() =>
    cutCrate(level, seeded(Date.now() + level * 7919)).map((p) => ({ cells: turned(p.cells, p.turns), at: null })),
  );
  const pieces = useRef<PieceState[]>(first);
  const drag = useRef<{ i: number; x: number; y: number } | null>(null);
  const pressed = useRef<{ i: number; x: number; y: number; from: 'shelf' | 'crate'; grab: Pt } | null>(null);
  const turnedAt = useRef<{ i: number; at: number } | null>(null);
  const nopeAt = useRef(-1e9);
  // Turning a block never changes its longest side, so the shelf's slots stay put all round.
  const [L] = useState(() => packLayout(lv.cols, lv.rows, c, dimsOf(first)));
  const total = lv.cols * lv.rows;

  const round = useRound(props);

  // Covered: whatever is in hand goes back to the shelf.
  useEffect(() => {
    if (!props.paused) return;
    pressed.current = null;
    drag.current = null;
  }, [props.paused]);

  const set = (i: number, change: Partial<PieceState>) => {
    pieces.current = pieces.current.map((p, j) => (j === i ? { ...p, ...change } : p));
  };
  const check = () => {
    const filled = pieces.current.reduce((s, p) => s + (p.at ? p.cells.length : 0), 0);
    round.progress(filled / total);
    if (filled === total && pieces.current.every((p) => p.at)) round.win();
  };

  const down = (p: Pt) => {
    if (!round.live) return;
    const list = pieces.current;
    if (p.y >= L.trayTop - 4) {
      // Which slot on the shelf: the nearest, within its half-width and the gap.
      let hit = -1;
      for (let i = 0; i < list.length; i++) {
        const slot = L.slot(i);
        if (!list[i]!.at && p.x >= slot.left - L.gap / 2 && p.x <= slot.right + L.gap / 2) hit = i;
      }
      if (hit >= 0) pressed.current = { i: hit, x: p.x, y: p.y, from: 'shelf', grab: { x: 0, y: 0 } };
      return;
    }
    const col = Math.floor((p.x - L.gx) / c);
    const row = Math.floor((p.y - L.gy) / c);
    const i = list.findIndex((b) => b.at && b.cells.some(([cc, rr]) => b.at![0] + cc === col && b.at![1] + rr === row));
    if (i < 0) return;
    const at = list[i]!.at!;
    pressed.current = { i, x: p.x, y: p.y, from: 'crate', grab: { x: p.x - (L.gx + at[0] * c), y: p.y - (L.gy + at[1] * c) } };
  };

  const move = (p: Pt) => {
    const from = pressed.current;
    if (!from || !round.live) return;
    if (!drag.current && Math.hypot(p.x - from.x, p.y - from.y) <= DRAG) return;
    const [w, h] = extent(pieces.current[from.i]!.cells);
    const at = from.from === 'shelf' ? { x: p.x - (w * c) / 2, y: p.y - h * c - LIFT } : { x: p.x - from.grab.x, y: p.y - from.grab.y };
    if (!drag.current && from.from === 'crate') {
      set(from.i, { at: null });
      check();
    }
    drag.current = { i: from.i, ...at };
  };

  const up = () => {
    const from = pressed.current;
    pressed.current = null;
    if (!from || !round.live) return;
    const d = drag.current;
    if (d) {
      drag.current = null;
      const spot = settleAt(pieces.current[d.i]!.cells, d.x, d.y, L, filledBy(pieces.current, d.i));
      set(d.i, { at: spot });
      if (spot) round.hit();
      else if (d.y < L.trayTop - 20) nopeAt.current = round.time();
      check();
      return;
    }
    // A tap: on the shelf it turns the block; in the crate it takes it out.
    if (from.from === 'shelf') {
      set(from.i, { cells: turn(pieces.current[from.i]!.cells) });
      turnedAt.current = { i: from.i, at: round.time() };
    } else {
      set(from.i, { at: null });
      check();
    }
  };

  const now = round.now;
  const d = drag.current;
  const turning = turnedAt.current && now - turnedAt.current.at < TURN_MS ? turnedAt.current : null;
  const view: PackView = {
    t: q(now),
    g: [lv.cols, lv.rows, c],
    b: pieces.current.map(pieceCode),
    d: d ? [d.i, q(d.x), q(d.y)] : null,
    tn: turning ? [turning.i, q((now - turning.at) / TURN_MS, 2)] : null,
    no: now - nopeAt.current < NOPE_MS,
    lid: round.won ? q(round.clear, 2) : null,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Crate and blocks', 'Kasse og blokker'), down, move, up }}
    />
  );
}

/**
 * The how-to, on round 1's crate with one block already in: the finger drags the second block in,
 * taps the third on the shelf to turn it the right way, drags it into the last gap, and the lid
 * comes down. The block in hand follows the finger by the board's own lift.
 */
function demoPack(): DemoScript<PackView> {
  const c = CELL[0];
  const bar: PieceState = { cells: normalise([[0, 0], [1, 0], [2, 0]]), at: [0, 0] };
  const ell: PieceState = { cells: normalise([[0, 1], [0, 2], [1, 2]]), at: null };
  const hook = normalise([[1, 1], [2, 1], [2, 2]]);
  const L = packLayout(3, 3, c, dimsOf([bar, ell, { cells: hook }]));
  const code = (cells: readonly Cell[], at: Cell | null = null) => pieceCode({ cells, at });
  /** Where the finger is when the block it carries has its top left on `at`. */
  const carry = (cells: readonly Cell[], at: Cell) => {
    const [w, h] = extent(cells);
    return { x: q(L.gx + at[0] * c + (w * c) / 2), y: q(L.gy + at[1] * c + h * c + LIFT) };
  };
  /** The block's top left for the finger at `p`. */
  const held = (cells: readonly Cell[], p: Pt) => {
    const [w, h] = extent(cells);
    return [q(p.x - (w * c) / 2, 1), q(p.y - h * c - LIFT, 1)] as const;
  };
  const along = (a: Pt, b: Pt, f: number) => ({ x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f });

  // The second block: pressed on the shelf, carried up, let go over its place.
  const shelf1 = L.slot(1);
  const into1 = carry(ell.cells, [0, 1]);
  const [press1, lift1, drop1] = [600, 700, 1_700];
  // The third: tapped to turn, then carried into the last gap.
  const shelf2 = L.slot(2);
  const into2 = carry(hook, [1, 1]);
  const turnAt = 2_850;
  const [press2, lift2, drop2] = [3_400, 3_500, 4_400];
  const full = drop2 + 100;

  const keys: DemoKey<PackView>[] = [
    { at: 0, view: { t: 0, g: [3, 3, c], b: [code(bar.cells, bar.at), code(ell.cells), code(turned(hook, 3))], d: null, tn: null, no: false, lid: null } },
    { at: lift1, view: { d: [1, ...held(ell.cells, along(shelf1, into1, (lift1 - press1) / (drop1 - press1)))] }, ease: 'step' },
    { at: drop1, view: { d: [1, ...held(ell.cells, into1)] } },
    { at: drop1 + 100, view: { d: null, b: [code(bar.cells, bar.at), code(ell.cells, [0, 1]), code(turned(hook, 3))], fx: [1, 0] }, ease: 'step' },
    { at: drop1 + 550, view: { fx: [0, 0] } },
    { at: turnAt, view: { b: [code(bar.cells, bar.at), code(ell.cells, [0, 1]), code(hook)], tn: [2, 0] }, ease: 'step' },
    { at: turnAt + TURN_MS, view: { tn: [2, 1] } },
    { at: turnAt + TURN_MS + 10, view: { tn: null }, ease: 'step' },
    { at: lift2, view: { d: [2, ...held(hook, along(shelf2, into2, (lift2 - press2) / (drop2 - press2)))] }, ease: 'step' },
    { at: drop2, view: { d: [2, ...held(hook, into2)] } },
    { at: full, view: { d: null, b: [code(bar.cells, bar.at), code(ell.cells, [0, 1]), code(hook, [1, 1])], lid: 0, fx: [1, 0] }, ease: 'step' },
    { at: full + 450, view: { fx: [0, 0], lid: q(450 / CLEAR_MS, 2) } },
    { at: full + CLEAR_MS, view: { lid: 1 } },
  ];
  const finger: FingerKey[] = [
    { at: 0, x: shelf1.x + 40, y: 312, hide: true },
    { at: 250, x: shelf1.x + 40, y: 312, hide: false },
    { at: press1 - 50, x: shelf1.x, y: shelf1.y },
    { at: press1, x: shelf1.x, y: shelf1.y, down: true },
    { at: drop1, x: into1.x, y: into1.y, ease: 'linear' },
    { at: drop1 + 100, x: into1.x, y: into1.y, down: false },
    { at: turnAt - 400, x: shelf2.x, y: shelf2.y },
    { at: turnAt - 150, x: shelf2.x, y: shelf2.y, down: true },
    { at: turnAt, x: shelf2.x, y: shelf2.y, down: false },
    { at: press2, x: shelf2.x, y: shelf2.y, down: true },
    { at: drop2, x: into2.x, y: into2.y, ease: 'linear' },
    { at: full, x: into2.x, y: into2.y, down: false },
    { at: 5_400, x: into2.x + 40, y: 312, hide: true },
  ];
  return {
    ms: 7_000,
    keys,
    finger,
    steps: [
      { at: 0, en: 'DRAG A BLOCK INTO THE CRATE', no: 'DRA EN BLOKK INN I KASSEN' },
      { at: 2_300, en: 'TAP A BLOCK TO TURN IT', no: 'TRYKK PÅ EN BLOKK FOR Å SNU' },
      { at: 3_300, en: 'FILL THE CRATE', no: 'FYLL KASSEN' },
    ],
  };
}

export const game = thirtyGame<PackView>({
  Board,
  View,
  demo: demoPack(),
  mistakes: false,
  words: {
    en: {
      instruction: 'Drag the blocks into the crate until it is full. Tap a block to turn it.',
      howMove: 'Drag each block into the crate. Tap a block on the shelf to turn it.',
    },
    no: {
      instruction: 'Dra blokkene inn i kassen til den er full. Trykk på en blokk for å snu den.',
      howMove: 'Dra hver blokk inn i kassen. Trykk på en blokk på hylla for å snu den.',
    },
  },
});
